import React, { useEffect, useRef, useState } from 'react';
import {
  BodyText,
  Button,
  Checkbox,
  Div,
  Flex,
  GridBag,
  Gutter,
  Icon,
  InlineNotification,
  Input,
  SearchInputWithScope,
  Table,
} from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { AbstractProductOverview, AdminCategory, SalesChannel, VariantOverview } from '@cdaas/api-clients/dist/product-service-openapi';

import { getMainSiteNavigationComponentHeight } from '../../../App/Header/Header';
import { Errors, VariantEverything } from '../utils';
import {
  formulateBundleMainItemOrAddonsSummaryErrorMessage,
  formulateSingleVariantErrorMessage,
  VariantEverythingWithAutomaticPricingErrors,
} from './utils';
import { productServiceAdminProductsClient, productServiceAdminProductVariantsClient } from '../../../../api-client/product-service-client';

import './MainProduct.scss';

interface MainProductProps {
  mainItemOptionIds: number[] | undefined;
  handleMainItemOptionIdsChange: (optionIds: number[]) => void;
  variants: VariantEverythingWithAutomaticPricingErrors[];
  handleVariantsChange: (variants: (VariantOverview | VariantEverything)[]) => Promise<boolean>;
  allCategoriesOfSystem: AdminCategory[];
  erroneousSalesChannels: SalesChannel[];
  error: Errors['mainProductVariants'];
}

function MainProduct(props: MainProductProps) {
  const { mainItemOptionIds, handleMainItemOptionIdsChange, variants, handleVariantsChange, erroneousSalesChannels, allCategoriesOfSystem, error } =
    props;
  const { t } = useTranslation('bundlescreen');

  const history = useHistory();

  const mainVariantsTableRef = useRef<HTMLTableElement>(null);
  const addNewMainVariantsFormRef = useRef<HTMLDivElement>(null);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const scrollToMainVariantsTable = () => {
    if (mainVariantsTableRef.current !== null) {
      const pos = mainVariantsTableRef.current.offsetTop - getMainSiteNavigationComponentHeight();
      window.scrollTo({ top: pos, behavior: 'smooth' });
    }
  };
  const scrollToAddNewMainVariantsForm = () => {
    if (addNewMainVariantsFormRef.current !== null) {
      const pos = addNewMainVariantsFormRef.current.offsetTop - getMainSiteNavigationComponentHeight();
      window.scrollTo({ top: pos, behavior: 'smooth' });
    }
  };

  const [searchForNewProductVariantOpen, setSearchForNewProductVariantOpen] = useState<boolean>(false);
  const [searchCategory, setSearchCategory] = useState<AdminCategory>(allCategoriesOfSystem[0]);
  const [productSearchInput, setProductSearchInput] = useState<string>('');
  const [currentProductOptions, setCurrentProductOptions] = useState<AbstractProductOverview[]>([]);
  const [currentProductVariantOptions, setCurrentProductVariantOptions] = useState<(VariantOverview & { duplicate: boolean })[]>([]);
  const [variantsSearchInput, setVariantsSearchInput] = useState<string>('');
  const [showingVariantOptions, setShowingVariantOptions] = useState<boolean>(false);
  const [searchForProductsError, setSearchForProductsError] = useState<boolean>(false);
  const [fetchProductError, setFetchProductError] = useState<boolean>(false);

  // Product variants that are already part of main part of bundle -> used for batch deleting
  const [selectedVariants, setSelectedVariants] = useState<number[]>([]);
  // Product variants that are possibly added to be part of main part of bundle
  const [selectedNewVariants, setSelectedNewVariants] = useState<VariantOverview[]>([]);

  useEffect(() => {
    if (initialLoad) {
      // Dont scroll when bundle page is opened
      setInitialLoad(false);
    } else if (searchForNewProductVariantOpen) {
      scrollToAddNewMainVariantsForm();
    } else {
      scrollToMainVariantsTable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchForNewProductVariantOpen]);

  const handleSearchCategoryChange = (searchCategoryId: number) => {
    const newSearchCategory = allCategoriesOfSystem.find(category => category.id === searchCategoryId);
    if (newSearchCategory) setSearchCategory(newSearchCategory);
  };

  const checkAllVariantsSelected = () => {
    return mainItemOptionIds?.length === selectedVariants.length;
  };
  const checkVariantSelected = (variantId: number) => {
    return selectedVariants.findIndex(selectedVariantId => selectedVariantId === variantId) !== -1;
  };
  const handleSelectAllVariants = () => {
    setSelectedVariants(mainItemOptionIds || []);
  };
  const handleDeselectAllVariants = () => {
    setSelectedVariants([]);
  };
  const handleSelectVariant = (variantId: number) => {
    setSelectedVariants([...selectedVariants, variantId]);
  };
  const handleDeselectVariant = (variantId: number) => {
    const updatedSelectedVariants = selectedVariants.filter(selectedVariantId => selectedVariantId !== variantId);
    setSelectedVariants(updatedSelectedVariants);
  };

  const handleSelectedVariantsRemove = async () => {
    const updatedMainItemVariantIds = (mainItemOptionIds || []).filter(variantId => !selectedVariants.includes(variantId));
    const updatedVariants = variants.filter(variant => !selectedVariants.includes(variant.id));
    const success = await handleVariantsChange(updatedVariants);
    if (success) {
      handleMainItemOptionIdsChange(updatedMainItemVariantIds);
      handleDeselectAllVariants();
    }
  };

  const handleCancelAddNewVariants = () => {
    setSearchForNewProductVariantOpen(false);
    setShowingVariantOptions(false);
    setCurrentProductVariantOptions([]);
    setSelectedNewVariants([]);
    setProductSearchInput('');
    setVariantsSearchInput('');
  };

  const handleAddNewVariants = async (newVariants: VariantOverview[]) => {
    // Make sure same variant is not added multiple times to same bundle
    const allVariantIdsOfBundle = variants.map(variant => variant.id);
    const filteredNewVariants = newVariants.filter(variant => !allVariantIdsOfBundle.includes(variant.id));

    const updatedVariants = [...variants, ...filteredNewVariants];
    const updatedMainItemVariantIds = [...(mainItemOptionIds || []), ...filteredNewVariants.map(variant => variant.id)];
    const success = await handleVariantsChange(updatedVariants);
    if (success) {
      handleMainItemOptionIdsChange(updatedMainItemVariantIds);
      handleCancelAddNewVariants();
    }
  };

  const checkNewVariantSelected = (variant: VariantOverview) => {
    return selectedNewVariants.findIndex(selectedVariant => selectedVariant.id === variant.id) !== -1;
  };
  const handleSelectNewVariant = (variant: VariantOverview) => {
    setSelectedNewVariants([...selectedNewVariants, variant]);
  };
  const handleDeselectNewVariant = (variant: VariantOverview) => {
    const updatedSelectedNewVariants = selectedNewVariants.filter(selectedVariant => selectedVariant.id !== variant.id);
    setSelectedNewVariants(updatedSelectedNewVariants);
  };

  const handleProductChosen = async (productId: number, productName: string) => {
    try {
      const response = await productServiceAdminProductVariantsClient.getAdminProductVariants(productId);
      // Each product should have at least 1 variant - this is here just in case
      if (!response.data.variants || response.data.variants.length === 0) return;

      // Check if some variants are already part of bundle -> prevent bundle from having duplicate variants
      const variantsWithDuplicateInfo = response.data.variants.map(variant => {
        const allVariantIdsOfBundle = variants.map(variant => variant.id);
        return { ...variant, product_id: productId, duplicate: allVariantIdsOfBundle.includes(variant.id) };
      });

      // If product has only single variant and that is not already part of bundle -> just add that
      if (variantsWithDuplicateInfo.length === 1 && !variantsWithDuplicateInfo[0].duplicate) {
        await handleAddNewVariants(variantsWithDuplicateInfo);
      }
      // If product has multiple variants -> make user choose which variant(s) to add
      else {
        setCurrentProductVariantOptions(variantsWithDuplicateInfo);
        setProductSearchInput(productName);
        setCurrentProductOptions([]);
        setShowingVariantOptions(true);
      }
    } catch {
      setFetchProductError(true);
    }
  };

  const onProductSearchInputChange = async (searchInput: string) => {
    // TODO - improve: Request is made on every key press. Thus, if earlier is responded after latter then "ip" gives results for "i"
    // One solution could be canceling the previous request but this might not be possible if the auto generated client does not provide such functionality
    setProductSearchInput(searchInput);
    setShowingVariantOptions(false);
    setSelectedNewVariants([]);
    setFetchProductError(false);
    try {
      const response = await productServiceAdminProductsClient.getAdminProducts(undefined, undefined, undefined, searchInput, [searchCategory?.id]);
      setCurrentProductOptions(response.data.products);
      setSearchForProductsError(false);
    } catch {
      setSearchForProductsError(true);
    }
  };

  let errorText = '';
  if (searchForProductsError) {
    errorText = t('product_variants_section.main.search_products_error');
  } else if (fetchProductError) {
    errorText = t('product_variants_section.main.fetch_product_variants_error');
  }

  function isDefined<T>(argument: T | undefined): argument is T {
    return argument !== undefined;
  }
  const mainProductVariants = (mainItemOptionIds || []).map(variantId => variants.find(variant => variant.id === variantId)).filter(isDefined);

  const searchFilteredNewProductVariantOptions = currentProductVariantOptions.filter(variant => {
    const nameToSearch = variant.name.replaceAll('/', '').replaceAll(' ', '').toLowerCase();
    const search = variantsSearchInput.replaceAll(' ', '').toLowerCase();
    return nameToSearch.includes(search);
  });

  const summaryAutomaticPricingError = formulateBundleMainItemOrAddonsSummaryErrorMessage(t, erroneousSalesChannels);

  return (
    <Flex directionColumn>
      {selectedVariants.length > 0 && (
        <Div>
          <GridBag>
            <GridBag.Item md={6}>
              <BodyText fontWeight="bold">
                {t('product_variants_section.main.selected_product_variants_count', { count: selectedVariants.length })}
              </BodyText>
            </GridBag.Item>
            <GridBag.Item md={6} alignTextToRight={true}>
              <Button variant="destructive" onClick={() => handleSelectedVariantsRemove()} size="md">
                {t('product_variants_section.main.buttons.remove_product_variants')}
              </Button>
            </GridBag.Item>
          </GridBag>
          <Gutter size="sm" />
        </Div>
      )}
      <GridBag>
        <GridBag.Item md={12}>
          <Table refElement={mainVariantsTableRef} className="bundle_add_main_product_variants__variants_table">
            <thead>
              <Table.Row>
                <Table.HeaderCell key="checkbox">
                  <Checkbox
                    checked={mainItemOptionIds && mainItemOptionIds.length > 0 && checkAllVariantsSelected()}
                    onChange={() => (checkAllVariantsSelected() ? handleDeselectAllVariants() : handleSelectAllVariants())}
                  />
                </Table.HeaderCell>
                <Table.HeaderCell key="product-variant-name">
                  <Div alignTextToCenter>
                    <BodyText fontWeight="bold">{t('product_variants_section.main.product_variant_name_table_header')}</BodyText>
                  </Div>
                </Table.HeaderCell>
                <Table.HeaderCell key="product-variant-sku">
                  <Div alignTextToCenter>
                    <BodyText fontWeight="bold">{t('product_variants_section.main.product_variant_sku_table_header')}</BodyText>
                  </Div>
                </Table.HeaderCell>
                <Table.HeaderCell key="product-variant-main"></Table.HeaderCell>
              </Table.Row>
            </thead>
            <tbody>
              {mainProductVariants.map(variant => {
                const automaticPricingError = formulateSingleVariantErrorMessage(t, variant, true);
                return (
                  <React.Fragment key={variant.id}>
                    <Table.Row>
                      <Table.Cell>
                        <Checkbox
                          name={`select-main-item-variant-${variant.id}`}
                          checked={checkVariantSelected(variant.id)}
                          onChange={() => (checkVariantSelected(variant.id) ? handleDeselectVariant(variant.id) : handleSelectVariant(variant.id))}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <BodyText alignTextToCenter>{variant.name}</BodyText>
                      </Table.Cell>
                      <Table.Cell>
                        <BodyText alignTextToCenter>{variant.sku}</BodyText>
                      </Table.Cell>
                      <Table.Cell>
                        <Flex
                          clickableAppearance={true}
                          color="purple500"
                          onClick={() => history.push(`/product/${variant.product_id}`)}
                          centeredVertically
                        >
                          <BodyText>{t('product_variants_section.main.view_product')}</BodyText>
                          <Icon name="external" size="sm" margin={{ left: 'xs' }} />
                        </Flex>
                      </Table.Cell>
                    </Table.Row>
                    {automaticPricingError && (
                      <Table.Row className="bundle_add_main_product_variants__automatic_pricing_error_table_row">
                        <Table.Cell />
                        <Table.Cell colSpan={3}>
                          <InlineNotification
                            data-testid={`main-item-automatic-pricing-variant-${variant.id}-error`}
                            forceMultiline
                            title={automaticPricingError.title}
                            variant="warning"
                          >
                            {automaticPricingError.message}
                          </InlineNotification>
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </GridBag.Item>
      </GridBag>

      {error && (
        <BodyText size="sm" color="red600">
          {error}
        </BodyText>
      )}

      {/* Open search for adding new product variants to be part of bundle*/}
      {!searchForNewProductVariantOpen && (
        <Div>
          <Gutter size="sm" />
          <BodyText
            margin={{ right: 'md' }}
            clickableAppearance={true}
            color="purple500"
            onClick={async () => {
              // Main item already has variants - make sure new variants are from same product (bundle main item can have product variants only from single product)
              if (mainProductVariants.length > 0) {
                const productId = mainProductVariants[0].product_id || -1;
                const productName = mainProductVariants[0].name.split(' ')[0];
                await handleProductChosen(productId, productName);
              }
              setSearchForNewProductVariantOpen(true);
            }}
          >
            {`+ ${t('product_variants_section.main.add_variants')}`}
          </BodyText>
        </Div>
      )}

      {summaryAutomaticPricingError && (
        <>
          <Gutter />
          <InlineNotification
            data-testid="main-item-automatic-pricing-summary-error"
            forceMultiline
            title={summaryAutomaticPricingError.title}
            variant="warning"
          >
            {summaryAutomaticPricingError.message}
          </InlineNotification>
        </>
      )}

      {/* Search and add new product variants to be part of bundle*/}
      {searchForNewProductVariantOpen && (
        <Div
          refElement={addNewMainVariantsFormRef}
          data-testid="bundle-main-item-add-variants-form"
          backgroundColor="gray100"
          className="bundle_add_main_product_variants__search_and_add_variants_section_div"
        >
          <Div padding={{ vertical: 'md', horizontal: 'sm' }}>
            <GridBag>
              <GridBag.Item md={7}>
                <SearchInputWithScope
                  data-testid="bundle-main-item-products-search-input-with-scope"
                  showLabel={true}
                  label={t('product_variants_section.main.search_select_product_label')}
                  placeholder={t('product_variants_section.main.search_placeholder')}
                  disabled={mainProductVariants.length > 0}
                  title={mainProductVariants.length > 0 ? t('product_variants_section.main.search_disable_already_variants') : ''}
                  errorText={errorText}
                  searchValue={productSearchInput}
                  onSearchChange={(event, value) => onProductSearchInputChange(value)}
                  searchOptions={currentProductOptions.map(product => ({ label: product.name, value: product.id.toString() }))}
                  onSearchOptionSelect={(event, option) => handleProductChosen(Number(option.value), option.label)}
                  scopeOptions={allCategoriesOfSystem.map(category => ({ label: category.name, value: category.id.toString() }))}
                  selectedScope={
                    mainProductVariants.length > 0 ? { label: '', value: '' } : { label: searchCategory?.name, value: searchCategory?.id.toString() }
                  }
                  onScopeChange={(event, option) => handleSearchCategoryChange(Number(option.value))}
                />
              </GridBag.Item>
            </GridBag>

            {showingVariantOptions && (
              <Div>
                <Gutter />
                <Flex directionColumn>
                  <BodyText fontWeight="bold">{t('product_variants_section.main.choose_variants_info')}</BodyText>
                </Flex>
                <Gutter size="xs" />
                <GridBag>
                  <GridBag.Item md={6}>
                    <Input
                      name="main-item-variants-search-input"
                      label=""
                      className="bundle_add_main_product_variants__new_variants_search_input"
                      value={variantsSearchInput}
                      placeholder={t('product_variants_section.main.variants_search_placeholder')}
                      onChange={(event, value) => setVariantsSearchInput(value)}
                    />
                  </GridBag.Item>
                </GridBag>
                <Gutter />
                <Table className="bundle_add_main_product_variants__variants_table">
                  <tbody>
                    {searchFilteredNewProductVariantOptions.map((variant: VariantOverview & { duplicate: boolean }) => (
                      <Table.Row key={variant.id} title={variant.duplicate ? t('product_variants_section.main.duplicate_product_variant') : ''}>
                        <Table.Cell>
                          <Checkbox
                            name={`select-new-main-item-variant-${variant.id}`}
                            disabled={variant.duplicate}
                            checked={checkNewVariantSelected(variant)}
                            onChange={() => (checkNewVariantSelected(variant) ? handleDeselectNewVariant(variant) : handleSelectNewVariant(variant))}
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <BodyText alignTextToCenter>{variant.name}</BodyText>
                        </Table.Cell>
                        <Table.Cell>
                          <BodyText alignTextToCenter>{variant.sku}</BodyText>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </tbody>
                </Table>
              </Div>
            )}

            <Gutter />
            <GridBag>
              <GridBag.Item md={5}>
                <Flex>
                  <Button
                    variant="primary"
                    disabled={selectedNewVariants.length < 1}
                    title={selectedNewVariants.length < 1 ? t('product_variants_section.main.add_variants_no_variants_selected_title') : ''}
                    onClick={() => handleAddNewVariants(selectedNewVariants)}
                  >
                    {t('product_variants_section.main.select_product_variants_btn')}
                  </Button>
                  <Gutter />
                  <Flex flexItem alignBaseline>
                    <Button textVariantProps={{ color: 'black' }} size={'xl'} onClick={() => handleCancelAddNewVariants()} variant={'text'}>
                      {t('product_variants_section.main.cancel_selecting_product_variants')}
                    </Button>
                  </Flex>
                </Flex>
              </GridBag.Item>
            </GridBag>
          </Div>
        </Div>
      )}
    </Flex>
  );
}

export default React.memo(MainProduct);
